import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { jornadaControleAPI } from '@/lib/api';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Coffee, KeyRound, Loader2, AlertTriangle, Clock, UtensilsCrossed, LogOut, Send, CheckCircle } from 'lucide-react';

const JornadaContext = createContext({});

export const JornadaProvider = ({ children }) => {
  const { user, token, logout: authLogout } = useAuth();
  const [bloqueado, setBloqueado] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [almocoConfig, setAlmocoConfig] = useState(null);
  const [pendingBlock, setPendingBlock] = useState(false);
  const [jornadaStatus, setJornadaStatus] = useState(null);
  const [showExpedientePopup, setShowExpedientePopup] = useState(false);
  const [expedienteRespondido, setExpedienteRespondido] = useState(false);
  const [bloqueioInfo, setBloqueioInfo] = useState(null);
  const [solicitandoExtra, setSolicitandoExtra] = useState(false);
  const [solicitacaoEnviada, setSolicitacaoEnviada] = useState(false);
  const [jornadaHoje, setJornadaHoje] = useState(null);
  const [tempoTrabalhado, setTempoTrabalhado] = useState(null);
  const [relogio, setRelogio] = useState(null);
  const [horaExtra, setHoraExtra] = useState(null);
  const [jornadaSaida, setJornadaSaida] = useState(null);
  const [ocultarCronometro, setOcultarCronometro] = useState(false);
  const intervalRef = useRef(null);
  const jornadaIntervalRef = useRef(null);
  const currentPath = useRef(window.location.pathname);

  const emAlmoco = jornadaHoje?.status === 'almoco';

  useEffect(() => {
    const updatePath = () => { currentPath.current = window.location.pathname; };
    window.addEventListener('popstate', updatePath);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function() { origPush.apply(this, arguments); updatePath(); };
    window.history.replaceState = function() { origReplace.apply(this, arguments); updatePath(); };
    return () => {
      window.removeEventListener('popstate', updatePath);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const isOnNovaVenda = useCallback(() => {
    return window.location.pathname === '/vendas/nova';
  }, []);

  // Jornada do dia (login / pausa almoço / sair) — todos os perfis
  const carregarJornadaHoje = useCallback(async () => {
    if (!token || !user) return null;
    try {
      const res = await jornadaControleAPI.status();
      setJornadaHoje(res.data.jornada);
      setTempoTrabalhado(res.data.tempo_trabalhado_minutos);
      setAlmocoConfig(res.data.almoco_config);
      setHoraExtra(res.data.hora_extra || null);
      setJornadaSaida(res.data.jornada_saida || null);
      setOcultarCronometro(!!res.data.ocultar_cronometro);
      if (res.data.hora_atual_completa) {
        const [h, m, s] = res.data.hora_atual_completa.split(':').map(Number);
        setRelogio({ servidorSeg: h * 3600 + m * 60 + (s || 0), clienteMs: Date.now() });
      }
      return res.data;
    } catch (e) {
      return null;
    }
  }, [token, user]);

  useEffect(() => {
    if (!token || !user) return;
    carregarJornadaHoje();
    jornadaIntervalRef.current = setInterval(() => { if (!document.hidden) carregarJornadaHoje(); }, 60000);
    const onVisibility = () => { if (!document.hidden) carregarJornadaHoje(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (jornadaIntervalRef.current) clearInterval(jornadaIntervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, user, carregarJornadaHoje]);

  const iniciarPausaAlmoco = async () => {
    const res = await jornadaControleAPI.iniciarAlmoco();
    setSenha('');
    setErro('');
    await carregarJornadaHoje();
    return res.data;
  };

  const solicitarHoraExtra = async (dados = {}) => {
    const res = await jornadaControleAPI.solicitarHoraExtra(dados);
    await carregarJornadaHoje();
    return res.data;
  };

  const alternarCronometro = async (ocultar) => {
    const res = await jornadaControleAPI.alternarCronometro(ocultar);
    setOcultarCronometro(!!res.data.ocultar_cronometro);
    return res.data;
  };

  const checkJornadaBlock = useCallback(async () => {
    if (!token || !user) return;
    if (user.perfil === 'ADMIN' || user.perfil === 'BKO' || user.perfil === 'BKO_MAILING') return;

    try {
      const res = await jornadaControleAPI.statusJornada();
      const data = res.data;
      setBloqueioInfo(data);
      setJornadaStatus(data.jornada);

      if (data.perfil === 'ADMIN' || data.perfil === 'BKO' || data.perfil === 'BKO_MAILING' || data.sem_jornada) {
        setBloqueado(false);
        return;
      }

      if (data.bloqueado) {
        if (isOnNovaVenda() && !data.motivo?.includes('encerrou')) {
          setPendingBlock(true);
        } else {
          setBloqueado(true);
          // Mostrar senha para retorno do almoço/lanche
          const voltaAlmoco = data.jornada?.volta_almoco;
          const voltaLanche = data.jornada?.volta_lanche;
          const horaAtual = data.hora_atual;
          if (voltaAlmoco && horaAtual >= voltaAlmoco && data.motivo?.includes('almoco')) {
            setMostrarSenha(true);
          } else if (voltaLanche && horaAtual >= voltaLanche && data.motivo?.includes('lanche')) {
            setMostrarSenha(true);
          } else {
            setMostrarSenha(false);
          }
        }
      } else {
        setBloqueado(false);
        setMostrarSenha(false);
        setSolicitacaoEnviada(false);
      }
    } catch (e) {
      // Fallback para o endpoint antigo
      try {
        const res = await jornadaControleAPI.status();
        const data = res.data;
        setJornadaHoje(data.jornada);
        setAlmocoConfig(data.almoco_config);
      } catch (e2) {}
    }
  }, [token, user, isOnNovaVenda]);

  const triggerPendingBlock = useCallback(async () => {
    if (pendingBlock) {
      setPendingBlock(false);
      setBloqueado(true);
    }
  }, [pendingBlock]);

  useEffect(() => {
    if (!token || !user) return;
    if (user.perfil === 'ADMIN' || user.perfil === 'BKO' || user.perfil === 'BKO_MAILING') return;

    checkJornadaBlock();
    // Verifica a cada 60s; aba em segundo plano não consulta, mas revalida assim que volta ao foco
    intervalRef.current = setInterval(() => { if (!document.hidden) checkJornadaBlock(); }, 60000);
    const onVisibility = () => { if (!document.hidden) checkJornadaBlock(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, user, checkJornadaBlock]);

  const handleRetorno = async () => {
    if (!senha.trim()) {
      setErro('Digite sua senha');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      await jornadaControleAPI.retorno(senha);
      setBloqueado(false);
      setMostrarSenha(false);
      setSenha('');
      await carregarJornadaHoje();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Senha incorreta');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutWithJornada = async () => {
    try {
      await jornadaControleAPI.logout();
    } catch (e) {}
    authLogout();
  };

  const handleSolicitarHoraExtra = async () => {
    setSolicitandoExtra(true);
    try {
      const res = await jornadaControleAPI.solicitarHoraExtra();
      setSolicitacaoEnviada(true);
    } catch (e) {
      if (e.response?.data?.status === 'pendente') {
        setSolicitacaoEnviada(true);
      }
    } finally {
      setSolicitandoExtra(false);
    }
  };

  const getBlockIcon = () => {
    if (!bloqueioInfo) return <Coffee className="w-12 h-12 text-amber-400" />;
    const motivo = bloqueioInfo.motivo || '';
    if (motivo.includes('almoco')) return <UtensilsCrossed className="w-12 h-12 text-orange-400" />;
    if (motivo.includes('lanche')) return <Coffee className="w-12 h-12 text-amber-400" />;
    if (motivo.includes('encerrou')) return <LogOut className="w-12 h-12 text-red-400" />;
    return <Clock className="w-12 h-12 text-blue-400" />;
  };

  const getBlockBg = () => {
    if (!bloqueioInfo) return 'bg-[#1a1d29]';
    const motivo = bloqueioInfo.motivo || '';
    if (motivo.includes('almoco')) return 'bg-gradient-to-br from-orange-950 to-[#1a1d29]';
    if (motivo.includes('lanche')) return 'bg-gradient-to-br from-amber-950 to-[#1a1d29]';
    if (motivo.includes('encerrou')) return 'bg-gradient-to-br from-red-950 to-[#1a1d29]';
    return 'bg-gradient-to-br from-blue-950 to-[#1a1d29]';
  };

  const contextValue = {
    bloqueado, triggerPendingBlock, pendingBlock, handleLogoutWithJornada, jornadaStatus,
    jornadaHoje, tempoTrabalhado, relogio, emAlmoco, almocoConfig, iniciarPausaAlmoco, recarregarJornada: carregarJornadaHoje,
    horaExtra, jornadaSaida, solicitarHoraExtra, ocultarCronometro, alternarCronometro,
  };

  const bloqueioHorario = bloqueado && user && !['ADMIN', 'BKO', 'BKO_MAILING'].includes(user.perfil);
  // Pausa manual (botão "Pausa Almoço") libera com senha a qualquer momento; bloqueio por horário segue a regra da jornada
  const exibirSenha = bloqueioHorario ? mostrarSenha : emAlmoco;

  // Block overlay
  if (user && (bloqueioHorario || emAlmoco)) {
    return (
      <JornadaContext.Provider value={contextValue}>
        <div className={`fixed inset-0 z-[9999] ${emAlmoco && !bloqueioHorario ? 'bg-gradient-to-br from-orange-950 to-[#1a1d29]' : getBlockBg()} flex items-center justify-center`} data-testid="jornada-block-overlay">
          <div className="text-center max-w-md mx-auto p-8">
            {!exibirSenha ? (
              <>
                <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                  {getBlockIcon()}
                </div>
                <h1 className="text-3xl font-bold text-white mb-3">Acesso Bloqueado</h1>
                <p className="text-lg text-gray-300 mb-2">{bloqueioInfo?.motivo || 'Sistema em pausa'}</p>
                
                {bloqueioInfo?.volta_em && (
                  <div className="bg-white/10 border border-white/20 rounded-xl p-4 mt-4 mb-4">
                    <p className="text-gray-400 text-sm">
                      {bloqueioInfo.motivo?.includes('encerrou') ? 'Proximo turno as' : 'Retorno as'}
                    </p>
                    <p className="text-4xl font-bold text-white mt-1 font-mono">{bloqueioInfo.volta_em}</p>
                  </div>
                )}
                
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mt-2">
                  <p className="text-xs text-gray-400">Horario atual</p>
                  <p className="text-lg font-mono text-white">{bloqueioInfo?.hora_atual}</p>
                </div>

                {/* Solicitar Hora Extra */}
                {bloqueioInfo?.pode_solicitar_extra && !solicitacaoEnviada && (
                  <Button
                    onClick={handleSolicitarHoraExtra}
                    disabled={solicitandoExtra}
                    className="mt-6 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 text-lg w-full"
                    data-testid="solicitar-hora-extra-btn"
                  >
                    {solicitandoExtra ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Send className="mr-2 h-5 w-5" />}
                    Solicitar Hora Extra
                  </Button>
                )}

                {solicitacaoEnviada && (
                  <div className="mt-6 p-4 rounded-xl bg-green-900/30 border border-green-500/30">
                    <CheckCircle className="h-6 w-6 text-green-400 mx-auto mb-2" />
                    <p className="text-green-300 text-sm">Solicitacao enviada! Aguardando aprovacao do administrador...</p>
                  </div>
                )}

                {bloqueioInfo?.hora_extra_ativa && (
                  <div className="mt-4 p-4 rounded-xl bg-purple-900/30 border border-purple-500/30">
                    <CheckCircle className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-purple-300 text-sm">Hora extra aprovada ate {bloqueioInfo.jornada?.saida_efetiva}</p>
                  </div>
                )}

                {bloqueioInfo?.hora_extra_negada && (
                  <div className="mt-4 p-4 rounded-xl bg-red-900/30 border border-red-500/30">
                    <AlertTriangle className="h-6 w-6 text-red-400 mx-auto mb-2" />
                    <p className="text-red-300 text-sm">Sua solicitacao de hora extra foi negada pelo administrador.</p>
                  </div>
                )}

                <div className="bg-white/5 border border-white/10 rounded-lg p-3 mt-4">
                  <div className="flex items-center gap-2 text-amber-300 justify-center">
                    <AlertTriangle className="w-4 h-4" />
                    <p className="text-xs">O acesso sera liberado apos o horario com validacao de senha.</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={`w-24 h-24 ${emAlmoco ? 'bg-orange-500/20' : 'bg-red-500/20'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                  {emAlmoco ? <UtensilsCrossed className="w-12 h-12 text-orange-400" /> : <KeyRound className="w-12 h-12 text-red-400" />}
                </div>
                <h1 className="text-3xl font-bold text-white mb-3" data-testid="jornada-overlay-titulo">{emAlmoco ? 'Pausa de almoço' : 'Bem-vindo de volta!'}</h1>
                {emAlmoco && jornadaHoje?.almoco_inicio && (
                  <div className="bg-white/10 border border-white/20 rounded-xl p-4 mb-4">
                    <p className="text-gray-400 text-sm">Pausa iniciada às</p>
                    <p className="text-4xl font-bold text-white mt-1 font-mono" data-testid="jornada-almoco-inicio">{String(jornadaHoje.almoco_inicio).slice(0, 5)}</p>
                  </div>
                )}
                <p className="text-gray-300 mb-6">{emAlmoco ? 'Digite sua senha para encerrar a pausa e voltar ao trabalho' : 'Digite sua senha para retornar ao sistema'}</p>
                <div className="space-y-4">
                  <Input
                    type="password"
                    value={senha}
                    onChange={(e) => { setSenha(e.target.value); setErro(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleRetorno()}
                    placeholder="Digite sua senha do CRM"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-center text-lg h-12"
                    data-testid="jornada-return-password"
                    autoFocus
                  />
                  {erro && <p className="text-red-400 text-sm">{erro}</p>}
                  <Button
                    onClick={handleRetorno}
                    disabled={loading}
                    className={`w-full ${emAlmoco ? 'bg-orange-600 hover:bg-orange-700' : 'bg-red-600 hover:bg-red-700'} h-12 text-lg`}
                    data-testid="jornada-return-btn"
                  >
                    {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Lock className="mr-2 h-5 w-5" />}
                    {emAlmoco ? 'Voltar do almoço' : 'Validar e Retornar'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        {children}
      </JornadaContext.Provider>
    );
  }

  return (
    <JornadaContext.Provider value={contextValue}>
      {children}
    </JornadaContext.Provider>
  );
};

export const useJornada = () => {
  const context = useContext(JornadaContext);
  return context;
};
